import { getDealersByPartnerId } from "./dealers"
import { MOCK_PARTNERS } from "./partners"
import { mockId, mulberry32, pick, randInt } from "./random"

const CITIES: { city: string; country: string; lat: number; lng: number; tz: string }[] = [
  { city: "İstanbul", country: "Türkiye", lat: 41.0082, lng: 28.9784, tz: "Europe/Istanbul" },
  { city: "Ankara", country: "Türkiye", lat: 39.9334, lng: 32.8597, tz: "Europe/Istanbul" },
  { city: "İzmir", country: "Türkiye", lat: 38.4237, lng: 27.1428, tz: "Europe/Istanbul" },
  { city: "Bursa", country: "Türkiye", lat: 40.1826, lng: 29.0669, tz: "Europe/Istanbul" },
  { city: "Antalya", country: "Türkiye", lat: 36.8969, lng: 30.7133, tz: "Europe/Istanbul" },
  { city: "Konya", country: "Türkiye", lat: 37.8667, lng: 32.4833, tz: "Europe/Istanbul" },
  { city: "Adana", country: "Türkiye", lat: 37.0, lng: 35.3213, tz: "Europe/Istanbul" },
  { city: "Gaziantep", country: "Türkiye", lat: 37.0662, lng: 37.3833, tz: "Europe/Istanbul" },
]

const DISTRICTS = [
  "Merkez",
  "Sanayi Sitesi",
  "OSB",
  "Teknokent",
  "Kampüs",
  "Lojistik Merkezi",
  "Fabrika Sahası",
  "Depo Alanı",
]

const CUSTOMERS = [
  "Oklidis Lab",
  "Acme Enerji",
  "Turkcell AR-GE",
  "ENKA İnşaat",
  "Borusan Holding",
  "Eczacıbaşı",
  "Koç Holding",
  "Sabancı Enerji",
  "Doğuş Otomotiv",
  "Tüpraş Demo",
]

const PROJECT_SUFFIXES = ["HQ", "Kampüs", "Tesisi", "Sahası", "Merkezi", "Test Tesisi", "Demo"]

export interface MockSite {
  id: string
  userId: string
  name: string
  customer: string
  country: string
  city: string
  district: string
  addressLine: string
  latitude: number
  longitude: number
  timezone: string
  hasDevice: boolean
  deviceCount: number
  created: string
  lastModified: string
  partnerId: string | null
  partnerName: string | null
  /** ADR (2026-04-30) — Hiyerarşi: Partner > Dealer > Customer.
   *  Bayinin müşterisiyse `dealerId` set; partner'ın doğrudan müşterisiyse null. */
  dealerId: string | null
  dealerName: string | null
}

const SITES_SEED = 4242

function generate(): MockSite[] {
  const rng = mulberry32(SITES_SEED)
  const sites: MockSite[] = []
  const now = Date.now()
  for (let i = 0; i < 30; i++) {
    const loc = pick(rng, CITIES)
    const customer = pick(rng, CUSTOMERS)
    const suffix = pick(rng, PROJECT_SUFFIXES)
    const createdDaysAgo = randInt(rng, 15, 720)
    const createdAt = new Date(now - createdDaysAgo * 24 * 3600_000).toISOString()
    const modifiedDaysAgo = randInt(rng, 0, Math.min(createdDaysAgo, 30))
    const lastModified = new Date(now - modifiedDaysAgo * 24 * 3600_000).toISOString()
    const deviceCount = i < 3 ? 0 : randInt(rng, 2, 12)
    // ~%60 bir Partner altında, %40 Admin-owned (partnerId null).
    const partner = rng() < 0.6 ? pick(rng, MOCK_PARTNERS) : null
    // Partner'ı olan müşterilerin %70'i bir bayiye atanır (zincirli);
    // %30'u doğrudan partner müşterisi (dealerId null).
    let dealerId: string | null = null
    let dealerName: string | null = null
    if (partner && rng() < 0.7) {
      const partnerDealers = getDealersByPartnerId(partner.id)
      if (partnerDealers.length > 0) {
        const dealer = pick(rng, partnerDealers)
        dealerId = dealer.id
        dealerName = dealer.name
      }
    }
    sites.push({
      id: mockId(rng, "site"),
      userId: mockId(rng, "user"),
      name: `${customer} ${suffix}`,
      customer,
      country: loc.country,
      city: loc.city,
      district: pick(rng, DISTRICTS),
      addressLine: `${randInt(rng, 1, 999)}. Sokak No:${randInt(rng, 1, 250)}`,
      latitude: +(loc.lat + (rng() - 0.5) * 0.3).toFixed(6),
      longitude: +(loc.lng + (rng() - 0.5) * 0.3).toFixed(6),
      timezone: loc.tz,
      hasDevice: deviceCount > 0,
      deviceCount,
      created: createdAt,
      lastModified,
      partnerId: partner?.id ?? null,
      partnerName: partner?.name ?? null,
      dealerId,
      dealerName,
    })
  }
  return sites
}

/**
 * Org-ağacı demo müşterileri (ADR #034, 2026-07-17) — org ağacındaki düğümlere
 * bağlı sahalar. Böylece ottomotiv/GRP login'inde müşteri listesi boş kalmaz ve
 * "cihazı müşteriye deploy et" akışı demo org'lara ulaşır. `dealerId`/`partnerId`
 * org-id'dir. GRP artık L2 alt-partner (bayi değil) → GRP sahaları partner-direkt
 * müşteridir (`partnerId=org-grp`, `dealerId=null`).
 */
const DEMO_SITES: MockSite[] = [
  {
    id: "site-grp-demo-1",
    userId: "user-grp-demo-1",
    name: "Bursa OSB Tesisi",
    customer: "Bursa OSB Enerji",
    country: "Türkiye",
    city: "Bursa",
    district: "OSB",
    addressLine: "1. Cadde No:12",
    latitude: 40.1826,
    longitude: 29.0669,
    timezone: "Europe/Istanbul",
    hasDevice: false,
    deviceCount: 0,
    created: "2026-06-12T09:00:00Z",
    lastModified: "2026-06-20T09:00:00Z",
    partnerId: "org-grp",
    partnerName: "GRP",
    dealerId: null,
    dealerName: null,
  },
  {
    id: "site-grp-demo-2",
    userId: "user-grp-demo-2",
    name: "Balıkesir Lojistik Merkezi",
    customer: "Balıkesir Lojistik",
    country: "Türkiye",
    city: "Bursa",
    district: "Lojistik Merkezi",
    addressLine: "Sanayi Bulvarı No:48",
    latitude: 39.6484,
    longitude: 27.8826,
    timezone: "Europe/Istanbul",
    hasDevice: false,
    deviceCount: 0,
    created: "2026-06-14T09:00:00Z",
    lastModified: "2026-06-21T09:00:00Z",
    partnerId: "org-grp",
    partnerName: "GRP",
    dealerId: null,
    dealerName: null,
  },
  {
    id: "site-merkez-demo-1",
    userId: "user-merkez-demo-1",
    name: "İstanbul Merkez Şube",
    customer: "Ottomotiv Merkez Müşterisi",
    country: "Türkiye",
    city: "İstanbul",
    district: "Merkez",
    addressLine: "Atatürk Cad. No:5",
    latitude: 41.0082,
    longitude: 28.9784,
    timezone: "Europe/Istanbul",
    hasDevice: false,
    deviceCount: 0,
    created: "2026-06-15T09:00:00Z",
    lastModified: "2026-06-22T09:00:00Z",
    partnerId: "org-ottomotiv",
    partnerName: "Netzr",
    dealerId: "org-ottomotiv-bayi-merkez",
    dealerName: "Netzr Merkez Bayii",
  },
  {
    id: "site-ottomotiv-demo-1",
    userId: "user-ottomotiv-demo-1",
    name: "Ottomotiv Doğrudan Müşteri",
    customer: "Ottomotiv Kurumsal",
    country: "Türkiye",
    city: "İstanbul",
    district: "Teknokent",
    addressLine: "Teknopark No:3",
    latitude: 41.0255,
    longitude: 29.0123,
    timezone: "Europe/Istanbul",
    hasDevice: false,
    deviceCount: 0,
    created: "2026-06-16T09:00:00Z",
    lastModified: "2026-06-23T09:00:00Z",
    partnerId: "org-ottomotiv",
    partnerName: "Netzr",
    dealerId: null,
    dealerName: null,
  },
]

export const mockSites: MockSite[] = [...generate(), ...DEMO_SITES]

export function getMockSite(id: string): MockSite | undefined {
  return mockSites.find((s) => s.id === id)
}
